import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Library, ChevronRight, Loader2, Filter, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLocale } from '@parentpath/i18n';
import { useBlogCategories } from '../hooks/useBlogPosts';
import type { BlogPostWithCategory } from '../types';

// Helper function to strip HTML tags for excerpt preview
function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function BlogListPage() {
  const { currentPregnancy, currentSSW, appMode, lastCompletedPregnancy } = usePregnancyStore();
  const { categories } = useBlogCategories();
  const { locale } = useLocale();
  const [posts, setPosts] = useState<BlogPostWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showOnlyRelevant, setShowOnlyRelevant] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Detect baby mode
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';

  // Calculate baby age in days
  const babyAgeDays = useMemo(() => {
    if (!isBabyMode || !lastCompletedPregnancy?.actual_birth_date) return null;
    const birthDate = new Date(lastCompletedPregnancy.actual_birth_date);
    return Math.floor((new Date().getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
  }, [isBabyMode, lastCompletedPregnancy?.actual_birth_date]);

  // Format baby age for display
  const babyAgeLabel = useMemo(() => {
    if (babyAgeDays == null) return '';
    const months = Math.floor(babyAgeDays / 30);
    if (months >= 1) {
      const remainingDays = babyAgeDays - months * 30;
      return remainingDays > 0
        ? `${months} Mon. ${remainingDays} Tage`
        : `${months} Monat${months > 1 ? 'e' : ''}`;
    }
    const weeks = Math.floor(babyAgeDays / 7);
    if (weeks >= 1) return `${weeks} Woche${weeks > 1 ? 'n' : ''}`;
    return `${babyAgeDays} Tag${babyAgeDays !== 1 ? 'e' : ''}`;
  }, [babyAgeDays]);

  useEffect(() => {
    async function fetchPosts() {
      setIsLoading(true);
      try {
        let query = supabase
          .from('blog_posts')
          .select(`
            *,
            category:blog_categories(*)
          `)
          .eq('is_published', true)
          .eq('locale', locale)
          .order('published_at', { ascending: false });

        // Filter by categories if selected
        if (selectedCategories.length > 0) {
          query = query.in('category_id', selectedCategories);
        }

        if (isBabyMode) {
          // Baby mode: only show baby-relevant posts
          query = query.in('target_mode', ['baby', 'all']);

          // Filter by baby age relevance if enabled
          if (showOnlyRelevant && babyAgeDays != null) {
            query = query
              .or(`baby_age_min_days.is.null,baby_age_min_days.lte.${babyAgeDays}`)
              .or(`baby_age_max_days.is.null,baby_age_max_days.gte.${babyAgeDays}`);
          }
        } else {
          // Pregnancy mode: only show pregnancy-relevant posts
          query = query.in('target_mode', ['pregnancy', 'all']);

          // Filter by SSW relevance if enabled
          if (showOnlyRelevant && currentSSW) {
            query = query
              .or(`ssw_min.is.null,ssw_min.lte.${currentSSW}`)
              .or(`ssw_max.is.null,ssw_max.gte.${currentSSW}`);
          }
        }

        const { data, error } = await query.limit(50);

        if (error) throw error;

        // Additional client-side filtering for risk/child count (pregnancy mode only)
        let filteredData = data || [];
        if (!isBabyMode && showOnlyRelevant && currentPregnancy) {
          filteredData = filteredData.filter((post) => {
            if (post.risk_relevance === 'yes' && !currentPregnancy.is_high_risk) return false;
            if (post.risk_relevance === 'no' && currentPregnancy.is_high_risk) return false;
            if (post.child_count_relevance === 'single' && (currentPregnancy.child_count || 1) > 1) return false;
            if (post.child_count_relevance === 'multiple' && (currentPregnancy.child_count || 1) === 1) return false;
            return true;
          });
        }

        setPosts(filteredData as BlogPostWithCategory[]);
      } catch (err) {
        console.error('Error fetching posts:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchPosts();
  }, [selectedCategories, showOnlyRelevant, currentSSW, currentPregnancy, locale, isBabyMode, babyAgeDays]);

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const activeFiltersCount = selectedCategories.length + (showOnlyRelevant ? 1 : 0);

  // Determine if relevance toggle should be shown
  const showRelevanceToggle = isBabyMode ? babyAgeDays != null : !!currentSSW;

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Library className="w-8 h-8 text-white" />
          <h1 className="text-white text-2xl font-bold">Wissensarchiv</h1>
        </div>
        <button
          onClick={() => setShowFilterModal(true)}
          className="relative px-3 py-1.5 bg-pp-orange rounded-[5px] flex items-center gap-2"
        >
          <Filter className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-medium">Filter</span>
          {activeFiltersCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-pp-orange text-xs font-bold rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <Library className="w-12 h-12 text-white/30 mx-auto mb-3" />
          <p className="text-white/60">Keine Artikel gefunden</p>
          {(selectedCategories.length > 0 || showOnlyRelevant) && (
            <button
              onClick={() => {
                setSelectedCategories([]);
                setShowOnlyRelevant(false);
              }}
              className="mt-4 px-4 py-2 glass-effect rounded-[10px] text-white text-sm"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="block glass-effect rounded-[15px] overflow-hidden hover:bg-white/15 transition-all"
            >
              {post.image_url && (
                <div className="aspect-video w-full overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              )}
              <div className="p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {post.category && (
                    <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
                      {post.category.name}
                    </span>
                  )}
                  {!isBabyMode && post.ssw_min && post.ssw_max && (
                    <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
                      SSW {post.ssw_min}-{post.ssw_max}
                    </span>
                  )}
                </div>
                <h2 className="font-semibold text-white mb-1">
                  {post.title}
                </h2>
                {(post.excerpt || post.summary) && (
                  <p className="text-sm text-white/60 line-clamp-2">
                    {stripHtml(post.excerpt || post.summary || '')}
                  </p>
                )}
                <div className="flex items-center justify-end mt-3">
                  <ChevronRight className="w-4 h-4 text-pp-orange" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal &&
        createPortal(
          <>
            <div className="bottom-sheet-backdrop" onClick={() => setShowFilterModal(false)} />
            <div className="bottom-sheet-container">
              <div className="bottom-sheet-modal">
                {/* Header */}
                <div className="bottom-sheet-header">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                      <Filter className="w-5 h-5 text-pp-orange" />
                    </div>
                    <h2>Filter</h2>
                  </div>
                  <button
                    onClick={() => setShowFilterModal(false)}
                    className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="bottom-sheet-content space-y-6">
                  {/* Relevance Toggle - adapts to mode */}
                  {showRelevanceToggle && (
                    <div>
                      <h3 className="text-white font-medium mb-3">
                        {isBabyMode ? 'Baby-Alter-Filter' : 'SSW-Filter'}
                      </h3>
                      <button
                        onClick={() => setShowOnlyRelevant(!showOnlyRelevant)}
                        className="w-full flex items-center justify-between p-3 glass-effect rounded-[10px]"
                      >
                        <span className="text-white text-sm">
                          {isBabyMode
                            ? `Nur passend für Baby-Alter (${babyAgeLabel})`
                            : `Nur passend für SSW ${currentSSW}`}
                        </span>
                        <div
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            showOnlyRelevant ? 'bg-pp-orange' : 'bg-white/20'
                          }`}
                        >
                          <span
                            className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${
                              showOnlyRelevant ? 'left-6' : 'left-1'
                            }`}
                          />
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Categories */}
                  <div>
                    <h3 className="text-white font-medium mb-3">Kategorien</h3>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => toggleCategory(cat.id)}
                          className={`px-3 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                            selectedCategories.includes(cat.id)
                              ? 'bg-pp-orange text-white'
                              : 'glass-effect text-white/70 hover:text-white'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bottom-sheet-footer">
                  <button
                    onClick={() => {
                      setSelectedCategories([]);
                      setShowOnlyRelevant(false);
                    }}
                    className="flex-1 py-3 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors"
                  >
                    Zurücksetzen
                  </button>
                  <button
                    onClick={() => setShowFilterModal(false)}
                    className="flex-1 py-3 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
                  >
                    Anwenden
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
